// BandRow — the MVP's page-level row primitive. A band (Trade, Calculate,
// Market Snapshot, Pricing & Risk, Surface Fit, Model) is rendered as ONE
// horizontal row with the band label on the left and N instrument cells to
// the right. Mirrors immersive_gui/ui/src/components/Row.jsx and the
// composition pattern in App.jsx.
//
// Grid: [LABEL_W px] [repeat(N, minmax(COL_W, 1fr))]
// Label is small uppercase letter-spaced text. Cells flow horizontally.
// Separator colour matches Diletta's widget inner divider (#416077) so
// the band rhythm reads consistently with the rest of the platform.
//
// `cellShell` wraps each child in the dilettaCell card chrome so simple
// controls (Selects, EditableNumbers) sit on a visible surface. Set to
// false for cells whose content already provides its own card chrome
// (Market / Risk / SABR / Model).
import { FC, ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { DILETTA_BANNER_BG, DILETTA_DIVIDER, DILETTA_TITLE, dilettaCellSx } from './diletta-chrome';

export const BAND_LABEL_W = 120;
export const BAND_COL_W = 240;

export interface BandRowProps {
  label: string;
  n: number;
  children: ReactNode; // typically N cells (one per instrument)
  // Visual variant: 'header' renders the label uppercase/bolder against
  // the Diletta banner background (used for TRADE banner / section
  // separators). 'row' is the default.
  variant?: 'header' | 'row';
  // Wrap each child in the dilettaCell card chrome. Default true.
  // Set to false when the cell content already paints its own card
  // (Market / Risk / SABR / Model widgets).
  cellShell?: boolean;
  // Bottom border. Default true. Set false for inner rows within a
  // logical group — e.g. the Trade sub-rows where only the last
  // (Pay Type) carries the divider that closes the Trade block.
  divider?: boolean;
  // Optional right-side controls (e.g. add-instrument button on a header row).
  right?: ReactNode;
}

const BandRow: FC<BandRowProps> = ({
  label,
  n,
  children,
  variant = 'row',
  cellShell = true,
  divider = true,
  right,
}) => {
  const isHeader = variant === 'header';

  // Wrap each child cell in the Diletta cell shell so it reads as a
  // discrete card. React.Children.map keeps keys intact.
  const cells = cellShell ? <ChildShell>{children}</ChildShell> : children;

  return (
    <Box
      sx={{
        display: 'grid',
        // Fixed-width columns per MVP Row.jsx (`${LABEL_WIDTH}px repeat(${n},
        // ${COL_WIDTH}px)`). Cells don't grow to fill the page; the page
        // overflows horizontally if N gets too high. With N <= 4 the
        // grid sits well within a 1366px viewport.
        gridTemplateColumns: `${BAND_LABEL_W}px repeat(${n}, ${BAND_COL_W}px)`,
        columnGap: 1.25,
        rowGap: 0,
        alignItems: isHeader ? 'center' : 'stretch',
        backgroundColor: isHeader ? DILETTA_BANNER_BG : 'transparent',
        borderBottom: divider ? `1px solid ${DILETTA_DIVIDER}` : 'none',
        px: 1.5,
        // 4px each side gives equal breathing room above/below every row —
        // and crucially above/below every divider line — matching the
        // visual gap between entry fields.
        py: 0.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ overflow: 'hidden' }}>
        <Typography
          sx={{
            fontSize: 10,
            color: isHeader ? DILETTA_TITLE : 'text.secondary',
            letterSpacing: isHeader ? '0.14em' : '0.06em',
            textTransform: 'uppercase',
            fontWeight: isHeader ? 700 : 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {right}
      </Stack>
      {cells}
    </Box>
  );
};

// Wrap each child in the cell-shell card chrome. `children` is the list
// produced by instruments.map(...); we normalise it to an array and wrap
// each entry in a Box carrying the uniform cell chrome. Each wrapper gets a
// positional key (`cell-${i}`) — the underlying child already carries its
// own key={inst.id} from the page composition, so this positional key only
// identifies the chrome wrapper and never collides with it.
const ChildShell: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <>
      {(Array.isArray(children) ? children : [children]).map((child, i) => (
        <Box
          // The original child is itself a Box with key={inst.id} from the
          // page composition; we wrap it with a positional key.
          key={`cell-${i}`}
          sx={{
            ...dilettaCellSx,
            px: 0.75,
            py: 0.5,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {child}
        </Box>
      ))}
    </>
  );
};

export default BandRow;
